import { useCallback, useEffect, useRef, useState } from "react";
import { useMultiSiteContext } from "@hooks/use-site-context";
import { useScreenAlert } from "@hooks/use-screen-alert";
import { useUserContext } from "@hooks/use-auth-context";
import { useRefreshTokenMutation } from "@hooks/use-refresh-token-mutation";
import { fetchWithTokenRefresh } from "@hooks/shared/fetch-with-token-refresh";
import type { SmAiConversation, SmAiConversationHistory, SmAiMessage } from "./types";

/**
 * WP REST contract (namespace `smassets/v1`, see `AiController.php`):
 *   POST /ai/conversations           -> { id, title?, updatedAt }
 *   GET  /ai/conversations/{id}      -> { id, title?, updatedAt, messages: SmAiMessage[] }
 *   POST /ai/chat  { conversationId, message }
 *     -> streamed application/x-ndjson body: {"delta":"..."}\n ... {"done":true}\n
 * A 404 on any route means the AI feature isn't enabled/capable for this
 * site/user (same "unsupported, don't error" convention as useGetAggregateAssets).
 */

let localIdCounter = 0;
const nextLocalId = () => `local-${Date.now()}-${localIdCounter++}`;

/**
 * Drives a single active conversation. `conversationId` is controlled by the
 * caller (the widget owns which thread is selected + persistence/listing) —
 * pass `null` for a not-yet-created "new chat". `onConversationCreated` fires
 * once the first message lazily creates a conversation, so the caller can
 * adopt the new id (select it, cache it, refresh the thread list, etc.).
 */
export const useSmAiChat = (
  conversationId: string | null,
  onConversationCreated?: (conversation: SmAiConversation) => void
) => {
  const { setError } = useScreenAlert();
  const { user } = useUserContext();
  const { refreshUserToken } = useRefreshTokenMutation();
  const { getApiUrl } = useMultiSiteContext();

  const [messages, setMessages] = useState<SmAiMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  // Distinguishes "backend not enabled for this site" from a transient error so the
  // drawer can show a friendly empty state instead of a broken composer.
  const [available, setAvailable] = useState(true);

  const abortRef = useRef<AbortController | null>(null);
  // Mirrors `conversationId` for reads inside async callbacks so an in-flight
  // send always targets the thread active when it started, not a stale render.
  const conversationIdRef = useRef(conversationId);
  conversationIdRef.current = conversationId;
  // Set when `ensureConversation` lazily creates a thread mid-send, so the
  // history-load effect below (triggered by the resulting `conversationId`
  // prop change) doesn't wipe the in-flight optimistic/streaming messages.
  const skipNextHistoryLoadRef = useRef(false);

  const authedFetch = useCallback(
    (url: string, init: RequestInit = {}) =>
      fetchWithTokenRefresh(
        url,
        {
          ...init,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user?.authToken ?? ""}`,
            ...(init.headers ?? {}),
          },
        },
        () => refreshUserToken(user?.refreshToken ?? "")
      ),
    [user?.authToken, user?.refreshToken, refreshUserToken]
  );

  const ensureConversation = useCallback(async () => {
    if (conversationIdRef.current) return conversationIdRef.current;

    const response = await authedFetch(getApiUrl("wp-json/smassets/v1/ai/conversations"), {
      method: "POST",
      body: JSON.stringify({}),
    });
    if (response.status === 404) {
      setAvailable(false);
      return null;
    }
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const conversation: SmAiConversation = await response.json();
    conversationIdRef.current = conversation.id;
    skipNextHistoryLoadRef.current = true;
    onConversationCreated?.(conversation);
    return conversation.id;
  }, [authedFetch, getApiUrl, onConversationCreated]);

  // Load history whenever the active conversation changes (including back to "new chat").
  useEffect(() => {
    if (!conversationId) {
      setMessages([]);
      return;
    }
    if (skipNextHistoryLoadRef.current) {
      skipNextHistoryLoadRef.current = false;
      return;
    }

    let cancelled = false;
    setIsLoadingHistory(true);
    setMessages([]);

    authedFetch(getApiUrl(`wp-json/smassets/v1/ai/conversations/${conversationId}`))
      .then(async (response) => {
        if (cancelled) return;
        if (response.status === 404) {
          setAvailable(false);
          return;
        }
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const history: SmAiConversationHistory = await response.json();
        setMessages(history.messages ?? []);
      })
      .catch((err) => {
        if (!cancelled) setError("Error loading SM AI conversation: " + (err as Error)?.message);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingHistory(false);
      });

    return () => {
      cancelled = true;
    };
    // authedFetch/getApiUrl/setError are stable enough here; only react to the thread changing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  const stop = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setIsStreaming(false);
  }, []);

  const sendMessage = useCallback(
    async (content: string) => {
      const trimmed = content.trim();
      if (!trimmed || isStreaming) return;

      const userMessage: SmAiMessage = {
        id: nextLocalId(),
        role: "user",
        content: trimmed,
        createdAt: new Date().toISOString(),
      };
      const assistantMessage: SmAiMessage = {
        id: nextLocalId(),
        role: "assistant",
        content: "",
        createdAt: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, userMessage, assistantMessage]);
      setIsStreaming(true);

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const activeConversationId = await ensureConversation();
        if (!activeConversationId) {
          setMessages((prev) => prev.filter((m) => m.id !== assistantMessage.id));
          return;
        }

        const response = await authedFetch(getApiUrl("wp-json/smassets/v1/ai/chat"), {
          method: "POST",
          body: JSON.stringify({ conversationId: activeConversationId, message: trimmed }),
          signal: controller.signal,
        });

        if (response.status === 404) {
          setAvailable(false);
          setMessages((prev) => prev.filter((m) => m.id !== assistantMessage.id));
          return;
        }
        if (!response.ok || !response.body) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          const lines = buffer.split("\n");
          buffer = lines.pop() ?? "";

          for (const line of lines) {
            if (!line.trim()) continue;
            try {
              const chunk = JSON.parse(line);
              if (typeof chunk.delta === "string" && chunk.delta.length > 0) {
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === assistantMessage.id ? { ...m, content: m.content + chunk.delta } : m
                  )
                );
              }
            } catch {
              // Skip malformed/partial lines rather than aborting an otherwise-good stream.
            }
          }
        }
      } catch (err) {
        if ((err as Error)?.name === "AbortError") return;
        setError("Error sending message to SM AI: " + (err as Error)?.message);
        setMessages((prev) => prev.filter((m) => m.id !== assistantMessage.id));
      } finally {
        setIsStreaming(false);
        abortRef.current = null;
      }
    },
    [authedFetch, ensureConversation, getApiUrl, isStreaming, setError]
  );

  return {
    messages,
    isStreaming,
    isLoadingHistory,
    available,
    sendMessage,
    stop,
  };
};
